define('services/authService',[
    'lib/knockout',
    'lib/bluebird',
    'lib/lodash4',
    'config',
    'services/activeRequestTracker',
    'services/apiCore',
    'services/dataStore',
    'services/fileUploadService',
    'services/localLogger',
    'services/serverLogger',
    'util/getPcAuthToken'
], function (
    ko,
    Promise,
    _,
    config,
    requestTracker,
    apiCore,
    dataStore,
    uploadService,
    localLogger,
    serverLogger,
    getPcAuthToken
) {
    'use strict';

    var principalId   = ko.observable(null);
    var isInitialized = ko.observable(false);
    var userLocale    = ko.observable(null);
    var oAuthError    = ko.observable(false);

    var authorizations   = [];
    var namedPermissions = [];

    var PERM_KEY = 'perm:';
    var ROLE_KEY = 'role:';

    /**
     * Parses permissions strings based on the passed in key
     * @returns {Array}
     */
    function parsePermissions(key, permString) {
        permString = permString.toLowerCase();
        if ( permString.indexOf(key) === 0 ) {
            return permString.split(key)[1];
        }
        return null;
    }

    /**
     * Returns permissin string for role based permissions
     * @param role
     * @returns {Array}
     */
    function getRolePermission(role){
        return parsePermissions(ROLE_KEY, role);
    }

    /**
     * Returns permissions key for name based permissions
     * @param permission
     * @returns {Array}
     */
    function getNamePermission(permission){
        return parsePermissions(PERM_KEY, permission);
    }

    /**
     * Returns an object that contains the permissions themselves and either the role or name based permissions that
     * are associated with them based on the permission prefix.  If there is no permission prefix the name and role permissions
     * are combined and the string is compared to the combined permisions list.
     *
     * If there are no permission prefixes a warn flag will be set in order to show a deprecation warning in the console.
     *
     * @param auth
     * @returns {{auths: Array, permission: (Array|*), warn: boolean}}
     */
    function getAuthorizationsAndPermissions(auth){
        var namePerm = getNamePermission(auth),
            rolePerm = namePerm ? null : getRolePermission(auth),
            auths = namePerm ? ( namePerm ? namedPermissions :  namedPermissions.concat(authorizations)) : authorizations,
            perm = namePerm || rolePerm || auth,
            warn = !rolePerm && !namePerm;

        return {
            auths      : auths,
            permission : perm,
            warn       : warn
        };
    }

    /**
     * Returns an array of expanded permissions e.g. architect:user:editor,readonly,admin becomes
     * architect:user:editor, architect:user:readonly, and architect:user:admin
     * @param rawPermissions {Array} The array of permissions unexpanded
     * @returns {Array} The array of expanded permissions
     */
    function expandPermissionComponents(rawPermissions) {
        var expandedPermissionComponents = [];
        _.forEach(rawPermissions, function (rawPermission) {
            var components = rawPermission.split(',');
            if (components.length > 1) {
                // The first element will contain the root
                var firstPerm = components.shift();
                var lastColonIndex = firstPerm.lastIndexOf(':');
                if (lastColonIndex >= 0) {
                    var root = firstPerm.substr(0, lastColonIndex);
                    expandedPermissionComponents.push(firstPerm);
                    [].push.apply(expandedPermissionComponents, _.map(components, function (component) {
                        return root + ':' + component;
                    }));
                } else {
                    localLogger.warn('Invalid component permission format for: ' + rawPermission);
                }
            } else {
                expandedPermissionComponents.push(rawPermission);
            }
        });

        return expandedPermissionComponents;
    }

    /**
     * Locally logs a deprecation warning about use of permissions and role prefixes
     * @param permissionString
     */
    function logDeprecationMessage(permissionString){
        var DEPRECATION_MESSAGE = '[DEPRECATION WARNING]: The authorization format used is being deprecated.  Please use role:{1} or perm:{1}';
        localLogger.warn(DEPRECATION_MESSAGE.replace(/\{1\}/ig, permissionString));
    }

    function registerOauthSession() {
        var oAuthToken;

        try {
            oAuthToken = getPcAuthToken();

            if (oAuthToken.secure.token_type !== "bearer") {
                throw new Error('No OAuth token set.');
            }
        } catch (err) {
            localLogger.error('Unable to load OAuth credentials', err);
            oAuthError(true);
            return Promise.resolve(); //let the app continue, and display the error
        }

        apiCore.setOAuthBearerToken(oAuthToken.secure.access_token);
        uploadService.setOauthBearerToken(oAuthToken.secure.access_token);

        requestTracker.isLoggedIn(true);
        serverLogger.isLoggedIn(true);

        return apiCore.v2.get('/users/me?expand=authorization')
                .then(function (user) {
                    registerPermissions(user.authorization);
                    /*
                        principalId should always be set last. It's the official indicator that the user is logged in and the
                        application can begin making requests, checking permissions, etc. That means it's essential that all other
                        session data be set up before it is publicly registered.

                        Having the principalId before the above steps could cause some pieces of the app to not load
                        correctly.
                    */
                    principalId(user.id);
                    localLogger.debug('Logged in with user', user.id, user);
                    return oAuthToken.secure.access_token;
                });

    }

    function registerPermissions(user) {
        authorizations = _.chain(user.roles)
            .filter(function (role) {
                return role.name;
            }).map(function (role) {
                return role.name.toLowerCase();
            }).value();

        namedPermissions = _.map(expandPermissionComponents(user.permissions), function(perm){
            return perm.toLowerCase();
        });
    }

    function trimDivisionFromPermission(permission) {
        var permissionPieces = permission.split(":");
        if (permissionPieces.length > 3) {
            return permissionPieces.slice(0, 3).join(":");
        }
        return permission;
    }

    var init = function () {
        return registerOauthSession();
    };

    init().then(function () {
        isInitialized(true);
    }).catch(function (err) {
        localLogger.error('Failed to initialize authentication state', err);
    });


    var deprecationWarnings = function (permissionData) {
        var deprecationWarning = permissionData.warn || _.find(permissionData, { warn: true });

        if (deprecationWarning) {
            logDeprecationMessage(deprecationWarning.permission);
        }
    };

    /**
     * Authentication service. Handles user login,
     * storing information about the logged in user
     * and the user's authenticaiton token.
     * @exports services/authService
     */
    var authService = {
        /**
         * Flag indicating if initialization of the login state is complete
         */
        initialized: isInitialized,

        /**
         * Flag indicating principalId has been set (indicating that both authorizations and user id are ready for appropriate permissions checking)
         *
         * @returns {boolean} Ensures that principalId has a truthy value
         */
        isLoggedIn: ko.computed(function () {
            return !!principalId();
        }),

        /**
         * Get the user id for the currently logged in user.
         *
         * @returns {observable} obervable for the logged in user's id.
         */
        principalId: ko.computed(function () {
            return principalId();
        }),

        /**
         * Flag to indicated if we've encountered an OAuth error and should show the error screen in the embedded view.
         */
        oAuthError: ko.computed(function () {
            return oAuthError();
        }),

        /**
         * The IETF locale id for the currently logged in user.
         * @returns {string} Either the locale ( en-US, fr-FR, etc ) or null if no user is logged in yet.
         */
        userLocale: ko.computed(function () {
            return userLocale();
        }),

        /**
         * Checks to see if a user has a given authorization.
         *
         * @param {string|object|array} authorizations      The authorizations to check for
         *      If string or array, it is assumed that the required check is "any"
         * @param {array} authorizations.any                Permissions that the user must have at least one of (OR)
         * @param {array} authorizations.all                Permissions that the user must have all of (AND)
         * @param {boolean} authorizations.ignoreDivisions  Same as below
         * @param {boolean} ignoreDivisions                 Whether to ignore division checks
         *      Has no effect if the ACL toggle is off or a permission is not scoped
         *
         * @returns {boolean} true if the user has the authorization, false otherwise
         */
        hasAuthorization: function (authorizations, ignoreDivisions) {
            // we will convert the currently expected single string or array of strings into the new signature
            if (_.isString(authorizations)) {
                authorizations = {
                    any: [authorizations]
                };
            } else if (_.isArray(authorizations)) {
                if (_.every(authorizations, _.isString)) {
                    authorizations = {
                        any: authorizations
                    };
                } else if (authorizations.length === 1 && (authorizations[0].all || authorizations[0].any)) {
                    authorizations = authorizations[0];
                } else {
                    localLogger.error('unexpected hasAuthorization call ' + JSON.stringify(authorizations, null, 4));
                }
            }


            var getAuths = function (auths) {
                var permissionsData = _.map(auths, getAuthorizationsAndPermissions);

                deprecationWarnings(permissionsData);

                return permissionsData;
            };

            var hasAny = false;
            var hasAll = false;
            var authData;

            ignoreDivisions = authorizations.ignoreDivisions || ignoreDivisions;

            // if 'any' is not defined or empty, hasAny will be set to true
            if (authorizations.any && !_.isEmpty(authorizations.any)) {
                authData = getAuths(authorizations.any);

                hasAny = _.some(authData, function (permData) {
                    if (ignoreDivisions) {
                        return _.some(permData.auths, function (perm) {
                            var trimmedAuth = trimDivisionFromPermission(perm);
                            var trimmedPerm = trimDivisionFromPermission(permData.permission.toLowerCase());
                            return trimmedAuth === trimmedPerm;
                        });
                    }
                    return _.includes(permData.auths, permData.permission.toLowerCase());
                });
            } else {
                hasAny = true;
            }


            // if 'all' is not defined or empty, hasAll will be set to true
            if (authorizations.all && !_.isEmpty(authorizations.all)) {
                authData = getAuths(authorizations.all);

                hasAll = _.every(authData, function (permData) {
                    if (ignoreDivisions) {
                        return _.some(permData.auths, function (perm) {
                            var trimmedAuth = trimDivisionFromPermission(perm);
                            var trimmedPerm = trimDivisionFromPermission(permData.permission.toLowerCase());
                            return trimmedAuth === trimmedPerm;
                        });
                    }
                    return _.includes(permData.auths, permData.permission.toLowerCase());
                });
            } else {
                hasAll = true;
            }

            // every set must pass entirely
            return hasAny && hasAll;
        },

        /**
         * Checks to see if a user has any of the provided authorizations.
         * @param {array} authorizations the list of authorizations to check for
         * @returns {boolean} true if the user has any of the checkedAuthorizations, false otherwise
         */
        hasAnyAuthorization: function (authorizations) {
            //var warning = "[DEPRECATION WARNING]: hasAnyAuthorization is being deprecated.  Use hasAuthorization({ any: ['my', 'permission', 'strings'] }) as a direct replacement";
            //localLogger.warn(warning);

            // handle edge case where concat is used to bundle the original array of strings alongside new object signature
            var authorizationStrings = [];
            var authorizationObjects = [];
            _.each(authorizations, function (auth) {
                if (_.isString(auth)) {
                    authorizationStrings.push(auth);
                } else {
                    authorizationObjects.push(auth);
                }
            });

            var anyAuthStrings = authService.hasAuthorization({
                any: authorizationStrings
            });
            var anyAuthObjects = _.some(authorizationObjects, authService.hasAuthorization);

            var hasAuthStrings = !_.isEmpty(authorizationStrings);
            var hasAuthObjects = !_.isEmpty(authorizationObjects);
            var noAuthsDefined = !hasAuthStrings && !hasAuthObjects;

            return noAuthsDefined ||
                (hasAuthStrings && anyAuthStrings) ||
                (hasAuthObjects && anyAuthObjects);
        },

        /**
         * returns a list of all roles in the organization
         * @param {object} query
         * @returns {object} A promise object to handle the asynchronous response from the server
         */
        getRoles: function (query) {
            return apiCore.v2.get('/authorization/roles', query)
                .then(function (response) {
                    return response.entities;
                });
        },

        /**
         * returns a list of all users granted a role in the organization
         * @param {String} roleId
         * @param {object} options
         * @returns {object} A promise object to handle the asynchronous response from the server
         */
        getRoleSubjectGrants: function (roleId, options) {
            return apiCore.v2.get('/authorization/roles/' + roleId + '/subjectgrants', options)
                .then(function (response) {
                    return response;
                });
        },

        /**
         * returns a list of all users with a role in the organization
         * @param {String} roleId
         * @param {object} options
         * @returns {object} A promise object to handle the asynchronous response from the server
         */
        getRoleUsers: function (roleId, options) {
            return apiCore.v2.get('/authorization/roles/' + roleId + '/users', options)
                .then(function (response) {
                    return response;
                });
        },

        /** Get the division ids for these perms that you have access to
         * @param {string|array} perms
        */
        getAuthorizedDivisions: function (perms) {
            if (typeof perms === 'string') {
                perms = [perms];
            }
            perms = _.map(perms, function (p) { return p.toLowerCase(); });

            function getDivisionId(auth, perm) {
                if (_.startsWith(auth, perm)) {
                    var divisionId = auth.substr(perm.length + 1);
                    if (/^[a-zA-Z0-9-]{36}$/.exec(divisionId)) {
                        return divisionId;
                    }
                }
            }

            var allAuthorizations = namedPermissions.concat(authorizations);
            var divisionIds = {}; // handle duplicates
            _.each(allAuthorizations, function (auth) {
                _.each(perms, function (perm) {
                    var divisionId = getDivisionId(auth, perm);
                    if (divisionId) {
                        divisionIds[divisionId] = true;
                    }
                });
            });
            return _.keys(divisionIds);
        },
        /** Retrieves an array of all the users permissions
         * @param {string|array} perms
        */
       getRawPermissions: function () {
        return apiCore.v2
            .get("/users/me?expand=authorization")
            .then(function(response) {
                return response.authorization.permissions;
            });
       },
    };

    return authService;
});

